import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { EffortAccessMap, EffortLevel } from "@/api/admin-api"

import { EFFORT_LEVELS } from "@/api/admin-api"
import { AlertTriangle, Ban, Pencil, Shield, ShieldCheck, X } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"

import { AccessPolicyDialog } from "./access-policy-dialog"
import {
  buildSaveMap,
  computeAclSaveMap,
  computeRowState,
  computeToggleDisabledSaveMap,
  isRowConfigured,
} from "./effort-access-utils"

const EFFORT_LABEL_KEYS: Record<EffortLevel, string> = {
  none: "reasoning_mode.effort_none",
  minimal: "reasoning_mode.effort_minimal",
  low: "reasoning_mode.effort_low",
  medium: "reasoning_mode.effort_medium",
  high: "reasoning_mode.effort_high",
  xhigh: "reasoning_mode.effort_xhigh",
  max: "reasoning_mode.effort_max",
}

export type EffortAccessDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  effortAccess?: EffortAccessMap
  isLoading?: boolean
  /**
   * The stored map could not be loaded. Every save here is a whole-map PUT, so
   * treating a failed load as `{}` would wipe every other level's rule.
   */
  loadError?: boolean
  isSaving?: boolean
  /**
   * Base effort access map, for the per-model variant: rows without their own
   * policy inherit the corresponding base entry (matching backend layering).
   */
  inheritMap?: EffortAccessMap
  /** Persist the full map (whole-map PUT). Removing a key clears that effort. */
  onSave: (map: EffortAccessMap) => Promise<void>
}

export function EffortAccessDialog({
  open,
  onOpenChange,
  title,
  description,
  effortAccess,
  isLoading,
  loadError = false,
  isSaving,
  inheritMap,
  onSave,
}: EffortAccessDialogProps) {
  const { t } = useTranslation()
  const [editingEffort, setEditingEffort] = useState<EffortLevel | undefined>()

  const map = effortAccess ?? {}
  const editingPolicy = editingEffort ? map[editingEffort] : undefined

  const statusLabel = (state: ReturnType<typeof computeRowState>): string => {
    // Priority: hard-disabled beats everything else (own disable before an
    // inherited one, since both render identically but only one is possible
    // at a time — a row can't be both), then the explicit-open override
    // (opposite meaning of "restricted" despite also being a configured
    // entry), then a real restriction, then baseline inheritance.
    if (state.isInheritedDisabled) {
      return t("adminDashboard.effortAccess.inheritsDisabled", {
        defaultValue: "Disabled by the baseline policy",
      })
    }
    if (state.isDisabled) {
      return t("adminDashboard.effortAccess.disabled", { defaultValue: "Disabled for everyone" })
    }
    if (state.isExplicitlyOpen) {
      return t("adminDashboard.effortAccess.explicitlyOpen", {
        defaultValue: "Explicitly available to everyone (overrides the baseline)",
      })
    }
    if (state.isRestricted) {
      return t("adminDashboard.effortAccess.restricted", { defaultValue: "Restricted" })
    }
    if (state.inheritsConfigured) {
      return t("adminDashboard.effortAccess.inheritsBaseline", {
        defaultValue: "Restricted by the baseline policy",
      })
    }
    return t("adminDashboard.effortAccess.unrestricted", { defaultValue: "Available to everyone" })
  }

  const statusIcon = (state: ReturnType<typeof computeRowState>) => {
    if (state.isDisabled) return <Ban className="text-destructive size-4" />
    if (state.isExplicitlyOpen || state.isRestricted) {
      return <ShieldCheck className="text-success size-4" />
    }
    return <Shield className="text-muted-foreground size-4" />
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          <DialogBody>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : null}
            {!isLoading && loadError ? (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle>
                  {t("adminDashboard.access.loadErrorTitle", {
                    defaultValue: "Could not load the access policy",
                  })}
                </AlertTitle>
                <AlertDescription>
                  {t("adminDashboard.access.loadErrorBody", {
                    defaultValue:
                      "Nothing has been changed. Close the dialog and try again. If it keeps failing, you may not have permission to manage access for this resource.",
                  })}
                </AlertDescription>
              </Alert>
            ) : null}
            {!isLoading && !loadError ? (
              <div className="space-y-2">
                {EFFORT_LEVELS.map((effort) => {
                  const rowState = computeRowState(map, inheritMap, effort)
                  // An explicit `disabled: false` is a configured per-model
                  // reopen of a disabled baseline too, and must be clearable.
                  const configured = isRowConfigured(map[effort])
                  return (
                    <div
                      key={effort}
                      className="bg-card flex items-center justify-between rounded-lg border px-4 py-2 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        {statusIcon(rowState)}
                        <div className="flex flex-col">
                          <span className="font-medium">{t(EFFORT_LABEL_KEYS[effort])}</span>
                          <span className="text-muted-foreground text-xs">
                            {statusLabel(rowState)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!rowState.isDisabled}
                          disabled={isSaving}
                          aria-label={t("adminDashboard.effortAccess.enableToggle", {
                            defaultValue: "Enable this reasoning effort level",
                          })}
                          onCheckedChange={(checked) =>
                            void onSave(
                              computeToggleDisabledSaveMap({
                                map,
                                inheritMap,
                                effort,
                                nextEnabled: checked,
                              })
                            )
                          }
                        />
                        {configured && !rowState.isDisabled ? (
                          <Button
                            disabled={isSaving}
                            size="icon"
                            type="button"
                            variant="ghost"
                            aria-label={t("adminDashboard.effortAccess.clear", {
                              defaultValue: "Clear restriction",
                            })}
                            onClick={() => void onSave(buildSaveMap(map, effort, undefined))}
                          >
                            <X className="size-4" />
                          </Button>
                        ) : null}
                        <Button
                          disabled={isSaving}
                          size="icon"
                          type="button"
                          variant="outline"
                          aria-label={t("adminDashboard.effortAccess.edit", {
                            defaultValue: "Edit restriction",
                          })}
                          onClick={() => setEditingEffort(effort)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <AccessPolicyDialog
        isSaving={isSaving}
        loadError={loadError}
        open={editingEffort !== undefined}
        policy={editingPolicy}
        description={t("adminDashboard.effortAccess.policyDescription", {
          defaultValue: "Configure who may use this reasoning effort level.",
        })}
        title={t("adminDashboard.effortAccess.policyTitle", {
          defaultValue: "Effort access — {{level}}",
          level: editingEffort ? t(EFFORT_LABEL_KEYS[editingEffort]) : "",
        })}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setEditingEffort(undefined)
        }}
        onSave={async (policy) => {
          if (!editingEffort) return
          await onSave(computeAclSaveMap(map, editingEffort, policy))
          setEditingEffort(undefined)
        }}
      />
    </>
  )
}
